import { GenericField, InputProps } from "../Fields/GenericField";
import { useField } from "formik";
import { ComponentPropsWithoutRef } from "react";
import { Input } from "@/components/catalyst/input";
import { cn } from "@/lib/utils";
import { selectOnFocus } from "@/lib/Components/Form/Utils/selectOnFocus";

export type TextInputProps = InputProps & {
  inputProps?: ComponentPropsWithoutRef<"input">;
  onChange?: (val: number | null) => void;
  suffix?: string;
};

export function NumberInput({
  inputProps,
  onChange,
  suffix,
  ...props
}: TextInputProps) {
  const [field, meta, { setValue }] = useField(props.name);
  const { value: fieldVal } = field;
  const showError = !!meta.error && meta.touched;

  let value: string | null | number;
  if (fieldVal === undefined || fieldVal === null || isNaN(fieldVal)) {
    value = "";
  } else {
    value = fieldVal.toString() || "0";
  }
  return (
    <GenericField
      viewNode={<span>{value === "" ? "--" : value}</span>}
      {...props}
    >
      <span
        className={cn([
          // Basic layout
          "relative block w-full",
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          "dark:before:hidden",
          // Focus ring
          "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500",
          // Disabled state
          "has-data-disabled:opacity-50 has-data-disabled:before:bg-zinc-950/5 has-data-disabled:before:shadow-none",
          // Invalid state
          "has-data-invalid:before:shadow-red-500/10",
          // Hide number input spinners when suffix is present
          suffix &&
            "[&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none [&_input]:appearance-none",
        ])}
      >
        <Input
          {...field}
          onFocus={selectOnFocus}
          {...inputProps}
          invalid={showError}
          type="number"
          value={value?.toString() ?? ""}
          onChange={async (event) => {
            let fieldValue: string | number | null | undefined;

            if (event.target.value === "") {
              fieldValue = null;
            } else {
              fieldValue = parseFloat(event.target.value);
            }

            await setValue(fieldValue);
            onChange?.(fieldValue);
          }}
        />

        {suffix ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex max-w-[50%] items-center pr-3">
            <span className="truncate text-sm text-zinc-500">{suffix}</span>
          </div>
        ) : null}
      </span>
    </GenericField>
  );
}
